import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  getDashboardReportes,
  getReporteDatos,
  getReporteEstadisticas,
  reiniciarEncuesta,
  eliminarRespuesta,
  exportarExcel,
  descargarQR,
} from '../api/encuestas.api'

export const reportesKeys = {
  all: ['encuestas-reportes'] as const,
  dashboard: (params?: Record<string, unknown>) => [...reportesKeys.all, 'dashboard', params] as const,
  datos: (id: number, params?: Record<string, unknown>) => [...reportesKeys.all, 'datos', id, params] as const,
  estadisticas: (id: number) => [...reportesKeys.all, 'estadisticas', id] as const,
}

export function useDashboardReportes(params?: { grupo_laboratorio_id?: number }) {
  return useQuery({
    queryKey: reportesKeys.dashboard(params),
    queryFn: () => getDashboardReportes(params),
  })
}

export function useReporteDatos(encuestaId: number, params?: { page?: number; page_size?: number }) {
  return useQuery({
    queryKey: reportesKeys.datos(encuestaId, params),
    queryFn: () => getReporteDatos(encuestaId, params),
    enabled: encuestaId > 0,
  })
}

export function useReporteEstadisticas(encuestaId: number) {
  return useQuery({
    queryKey: reportesKeys.estadisticas(encuestaId),
    queryFn: () => getReporteEstadisticas(encuestaId),
    enabled: encuestaId > 0,
  })
}

export function useReiniciarEncuesta() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (encuestaId: number) => reiniciarEncuesta(encuestaId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reportesKeys.all })
      toast.success('Datos reiniciados correctamente')
    },
    onError: () => toast.error('Error al reiniciar datos'),
  })
}

export function useEliminarRespuesta(encuestaId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (sesionId: number) => eliminarRespuesta(encuestaId, sesionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reportesKeys.datos(encuestaId) })
      queryClient.invalidateQueries({ queryKey: reportesKeys.dashboard() })
      toast.success('Respuesta eliminada')
    },
    onError: () => toast.error('Error al eliminar respuesta'),
  })
}

export function useExportarExcelReporte() {
  return useMutation({
    mutationFn: (id: number) => exportarExcel(id),
    onSuccess: (blob) => {
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `encuesta_export_${new Date().toISOString().slice(0, 10)}.xlsx`
      a.click()
      URL.revokeObjectURL(url)
      toast.success('Excel descargado')
    },
    onError: () => toast.error('Error al exportar Excel'),
  })
}

export function useDescargarQRReporte() {
  return useMutation({
    mutationFn: (id: number) => descargarQR(id),
    onSuccess: (blob) => {
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `qr_encuesta_${new Date().toISOString().slice(0, 10)}.png`
      a.click()
      URL.revokeObjectURL(url)
    },
    onError: () => toast.error('Error al descargar QR'),
  })
}
